import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, HideText } from '@fashionnovateam/nova-ui-toolkit';
import queryString from 'querystring';
import decodeLocation from '../../Lib/Utils/Helpers/decode-location';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import FactoryDataGrid from './FactoryDataGrid.tsx';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';

/**
 * Creates the table headers from objects imported here.
 *
 * @param {arrayOf} headerArr array of header value objects
 *  @property {object} header individual objects with header values
 *    @property {string} label the label value
 *    @property {number} key unique key for react components
 *
 * @returns {object} header cell components to be used by the table.
 */

export default function VendorFactories(props) {
  const { currentUser } = props;
  const [params, setParams] = useState({
    ...decodeLocation(props.location.search),
    search: '',
    page: 1,
    sortBy: 'createdAt',
    sortOrder: 'DESC',
  });

  const seachValue =
    queryString.parse(props.location.search.slice(1)).search || '';

  useEffect(() => {
    props.getFactories(params, props.user.id);
  }, [params]);

  const perms = useMemo(() => {
    const canCreate = hasPermissionTo(
      'users/:id/factories',
      currentUser.role,
      Actions.CREATE,
      'FACTORY',
    );

    return {
      canCreate,
    };
  }, [currentUser]);

  return (
    <Page.Container>
      <Header.Major icon="building/building-apartment-one" title="Factories">
        {perms.canCreate && (
          <Button
            onClick={() =>
              props.history.push(`/users/${props.user.id}/factories/new`)
            }
            size="sm"
          >
            <Icon name="plus/plus-square" variant="solid" />
            <HideText notAbove="lg">
              Add{'\u00A0'}New{'\u00A0'}Factory
            </HideText>
          </Button>
        )}
      </Header.Major>
      <Header.Minor
        search={{
          value: seachValue,
          onChange: debounce(
            (value) => setParams((p) => ({ ...p, search: value })),
            500,
          ),
        }}
      />
      <Page.Content>
        <FactoryDataGrid
          userId={props.user.id}
          rows={props.factories}
          count={props.count}
          loading={props.loading}
          params={params}
          pageSizeOptions={[15]}
          paginationModel={{
            pageSize: 15,
            page: params.page - 1 ?? 0,
          }}
          onPaginationModelChange={(model) => {
            setParams((p) => ({ ...p, page: model.page + 1 }));
          }}
          sortModel={
            params?.sortBy
              ? [
                  {
                    field: params.sortBy,
                    sort: params.sortOrder === 'ASC' ? 'asc' : 'desc',
                  },
                ]
              : []
          }
          onSortModelChange={(model) => {
            const sortItem = model[0];
            setParams((p) => ({
              ...p,
              sortBy: sortItem?.field || undefined,
              sortOrder: sortItem?.sort.toUpperCase() || undefined,
            }));
          }}
        />
      </Page.Content>
    </Page.Container>
  );
}

VendorFactories.propTypes = {
  count: PropTypes.number.isRequired,
  factories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getFactories: PropTypes.func.isRequired,
  history: PropTypes.shape({ push: PropTypes.func }).isRequired,
  location: PropTypes.shape({ search: PropTypes.string }).isRequired,
  loading: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
  }).isRequired,
  currentUser: PropTypes.shape({}).isRequired,
};
